import React, { useEffect, useMemo, useState } from 'react';

const ECONOMIC_INDEX = [
  'packs',
  'classic-production',
  'classic-upgrades',
  'agri-v2',
  'agri-levels',
  'specializations',
  'pet-benefits',
  'stake',
  'conversion',
  'withdrawals',
];

const CONTENT = {
  es: {
    chooseLanguage: 'ELIGE TU IDIOMA',
    chooseHint: 'Después podrás cambiarlo en cualquier momento.',
    spanish: 'ESPAÑOL',
    english: 'ENGLISH',
    title: 'WHITEPAPER CITYFARM',
    subtitle: 'Guía de juego, progresión y economía',
    index: 'ÍNDICE',
    economicIndex: 'ÍNDICE ECONÓMICO',
    backIndex: 'VOLVER AL ÍNDICE',
    close: 'CERRAR',
    changeLanguage: 'IDIOMA',
    dynamic: 'VALOR DINÁMICO',
    dynamicNote: 'Las tasas de mercado, conversión, pool y algunas ofertas pueden cambiar. El juego muestra siempre el valor vigente antes de confirmar.',
    sections: [
      {
        id: 'intro',
        icon: '🌆',
        title: '1. Qué es CityFarm',
        body: [
          'CityFarm es un juego de granja, producción y economía donde cada jugador desarrolla su propio mundo.',
          'El progreso se basa en producir recursos, mejorar tierras e instalaciones, administrar inventario, cumplir objetivos y utilizar de forma estratégica las monedas del ecosistema.',
          'CityFarm V2 convierte las funciones principales en lugares físicos dentro del mapa para que la granja sea el centro de toda la experiencia.'
        ]
      },
      {
        id: 'entry',
        icon: '🚪',
        title: '2. Entrada, cuenta e idiomas',
        bullets: [
          'Inicio de sesión con la cuenta del jugador.',
          'Entrada al mundo CityFarm V2.',
          'Selector Español / English.',
          'Acceso a la versión clásica cuando esté habilitado.',
          'Cerrar sesión devuelve al login sin borrar progreso.'
        ]
      },
      {
        id: 'world',
        icon: '🗺️',
        title: '3. Mundo CityFarm V2',
        bullets: [
          'Mapa con carreteras, río, parcelas, árboles, animales, farolas y edificios.',
          'Movimiento del personaje por el mundo.',
          'Interacción por proximidad: ACÉRCATE / INTERACTUAR.',
          'Zoom, capas visuales, clima y ciclo horario.',
          'Servicios representados mediante edificios físicos.'
        ]
      },
      {
        id: 'game-loop',
        icon: '🔁',
        title: '4. Ciclo principal de juego',
        bullets: [
          'Elegir una tierra o instalación.',
          'Aportar semillas, agua, energía u otros recursos.',
          'Iniciar producción.',
          'Esperar el tiempo correspondiente.',
          'Recolectar.',
          'Reinvertir en mejoras, nuevas tierras, comercio o especializaciones.'
        ]
      },
      {
        id: 'resources',
        icon: '🎒',
        title: '5. Recursos y monedas',
        table: {
          headers: ['Recurso', 'Uso'],
          rows: [
            ['CF', 'Moneda/recurso interno utilizado en funciones del ecosistema y conversión.'],
            ['HUESO', 'Mejoras, desbloqueos, semillas y Agriculture V2.'],
            ['VIPHUESO', 'Packs y determinados requisitos de mejoras.'],
            ['Water', 'Producción agrícola, árboles y animales.'],
            ['Energy', 'Producción y funcionamiento de instalaciones.'],
            ['Carbon', 'Producción de energía y requisitos de mejora.'],
            ['Wood', 'Producido por árboles y usado en mejoras.'],
            ['Corn / Wheat', 'Producción agrícola base.'],
            ['Egg / Milk', 'Producción animal y requisitos de progresión.']
          ]
        }
      },
      {
        id: 'packs',
        icon: '📦',
        title: '6. Packs y semillas',
        table: {
          headers: ['Pack', 'Precio', 'Contenido principal'],
          rows: [
            ['Initial', '$2', 'Water 10 · Wheat seed 10 · Energy 2 · Wood 2 · Wheat land ID 3'],
            ['General', '$5', 'Water 40 · Corn seed 20 · Energy 8 · Wood 8 · Corn land ID 1'],
            ['Special', '$10', 'Water 60 · Wheat seed 50 · Corn seed 50 · Energy 30 · Carbon 30 · Wood 30 · Corn ID 2 · Wheat ID 4 · Tree ID 5 · Chicken coop ID 10'],
            ['Water production', '$15', 'Water 30 · Wheat seed 20 · Corn seed 20 · Energy 20 · Carbon 10 · Wood 10 · Tree ID 6 · Water well ID 8 · Cow farm ID 11'],
            ['Energy', '$20', 'Water 40 · Wheat seed 20 · Corn seed 20 · Energy 20 · Carbon 10 · Wood 10 · Tree ID 7 · Power plant ID 9'],
            ['Corn seed', '$0.10', '20 Corn seed'],
            ['Wheat seed', '$0.10', '20 Wheat seed']
          ]
        }
      },
      {
        id: 'classic-production',
        icon: '🌾',
        title: '7. Producción clásica',
        table: {
          headers: ['Instalación', 'Producción base', 'Tiempo / consumo'],
          rows: [
            ['Maíz', '1 semilla → 2 Corn', '24 h mostradas en interfaz · Water según tierra'],
            ['Trigo', '1 semilla → 2 Wheat', '24 h · Water según tierra'],
            ['Árbol', '5 Wood × nivel', '72 h · consume Water'],
            ['Pozo de agua', '1 Water por ciclo base', '4 h · 1 Energy · capacidad base 8'],
            ['Central de energía', '1 Energy por ciclo base', '4 h · 1 Carbon · capacidad base 8'],
            ['Vacas', '2 Milk × nivel', '24 h · 2 Wheat Bag × nivel + 3 Water × nivel'],
            ['Gallinero', '2 Egg × nivel', '24 h · 2 Corn Bag × nivel + 3 Water × nivel']
          ]
        }
      },
      {
        id: 'classic-upgrades',
        icon: '⬆️',
        title: '8. Mejoras clásicas',
        body: [
          'Las tierras e instalaciones clásicas utilizan una combinación de recursos, HUESO y VIPHUESO.',
          'Los requisitos aumentan por nivel. Las mejoras avanzadas consumen más Wood, Water, Energy, Carbon, Egg y Milk, además de HUESO/VIPHUESO.',
          'El molino dispone de progresión propia hasta nivel 5 y sus niveles reducen el tiempo de procesamiento.'
        ],
        table: {
          headers: ['Mejora', 'Referencia de costo HUESO / VIPHUESO'],
          rows: [
            ['LVL 1 → 2', '5,000 HUESO + 1 VIPHUESO, además de recursos específicos'],
            ['LVL 2 → 3', '5,000–10,000 HUESO + 1 VIPHUESO según instalación'],
            ['LVL 3 → 4', '30,000 HUESO + 5 VIPHUESO en instalaciones avanzadas'],
            ['Molino LVL 4 → 5', '50,000 HUESO + 10 VIPHUESO + recursos']
          ]
        }
      },
      {
        id: 'agri-v2',
        icon: '🚜',
        title: '9. Agriculture V2: seis parcelas',
        table: {
          headers: ['Parcela', 'Desbloqueo', 'Requisito', 'Tiempo base'],
          rows: [
            ['1', 'GRATIS', 'Inicial', '24 h'],
            ['2', '5,000 HUESO', '1 parcela desbloqueada', '24 h'],
            ['3', '5,000 HUESO', '2 parcelas desbloqueadas', '24 h'],
            ['4', '5,000 HUESO', '3 parcelas desbloqueadas', '24 h'],
            ['5', '50,000 HUESO', '4 parcelas + 25 cosechas totales', '18 h'],
            ['6', '100,000 HUESO', '5 parcelas + 75 cosechas totales', '12 h']
          ]
        },
        body: [
          'Cada parcela puede estar bloqueada, libre, produciendo o lista para cosechar.',
          'Los cultivos disponibles actualmente son maíz y trigo.',
          'Las cifras se obtienen de la configuración activa de Agriculture V2.'
        ]
      },
      {
        id: 'agri-levels',
        icon: '⭐',
        title: '10. Niveles de Agriculture V2',
        table: {
          headers: ['Mejora', 'Costo', 'Cosechas totales'],
          rows: [
            ['LVL 1 → 2', '10,000 HUESO', '20'],
            ['LVL 2 → 3', '20,000 HUESO', '60'],
            ['LVL 3 → 4', '40,000 HUESO', '100'],
            ['LVL 4 → 5', '80,000 HUESO', '200']
          ]
        },
        body: [
          'Subir de nivel requiere tanto HUESO como actividad real mediante cosechas.',
          'Los niveles mejoran tiempo, rendimiento y/o consumo de insumos según la etapa.'
        ]
      },
      {
        id: 'specializations',
        icon: '🌿',
        title: '11. Especializaciones',
        table: {
          headers: ['Especialización', 'Costo', 'Beneficio'],
          rows: [
            ['Invernadero', '50,000 HUESO', 'Tiempo ×0.85 · aproximadamente 15% más rápido'],
            ['Riego avanzado', '60,000 HUESO', 'Tiempo ×0.95 · insumos ×0.65'],
            ['Cultivo intensivo', '75,000 HUESO', 'Rendimiento ×1.25 · insumos ×1.15']
          ]
        },
        body: ['Requieren parcela nivel 5. Cada parcela puede tener una sola especialización permanente.']
      },
      {
        id: 'pet-benefits',
        icon: '🐶',
        title: '12. Mascota Big Dog Bone y beneficios',
        table: {
          headers: ['Nivel mascota', 'Producción general', 'Water'],
          rows: [
            ['1–11', '-1% tiempo', '-1%'],
            ['12–17', '-2%', '-1%'],
            ['18–23', '-2%', '-2%'],
            ['24–35', '-3%', '-2%'],
            ['36–47', '-4%', '-3%'],
            ['48–53', '-5%', '-3%'],
            ['54–59', '-5%', '-4%'],
            ['60+', '-6% máximo', '-4% máximo']
          ]
        },
        body: [
          'La mascota debe mantenerse en buenas condiciones para activar el beneficio.',
          'No duplica cosechas directamente: su beneficio principal reduce tiempos de producción.'
        ]
      },
      {
        id: 'inventory',
        icon: '🏬',
        title: '13. Almacén, objetos y tickets',
        body: [
          'El Almacén permite revisar recursos e inventario.',
          'Objetos / Tickets tiene edificio propio dentro del mundo para acceder a elementos especiales.',
          'Packs, inventario y objetos forman parte del ciclo de administración del jugador.'
        ]
      },
      {
        id: 'trade',
        icon: '🏪',
        title: '14. Mercado Trade',
        body: [
          'El Mercado Trade conecta la producción con el sistema de intercambio.',
          'Los precios y la disponibilidad pueden variar según el mercado activo.',
          'El jugador debe revisar la información vigente antes de confirmar cualquier intercambio.'
        ]
      },
      {
        id: 'stake',
        icon: '🏦',
        title: '15. Pool / Stake',
        table: {
          headers: ['Recurso', 'Puntos por unidad'],
          rows: [
            ['Water', '1'],
            ['Wood', '1'],
            ['Carbon', '0.25'],
            ['Wheat', '1'],
            ['Corn', '1'],
            ['Corn Bag', '2'],
            ['Wheat Bag', '2'],
            ['Egg', '3'],
            ['Milk', '3'],
            ['Energy', '0.5'],
            ['VIPHUESO', '0.05'],
            ['HUESO', '0.0005']
          ]
        },
        body: ['Los recursos depositados en Stake se transforman en puntos para participar en recompensas según las reglas vigentes.']
      },
      {
        id: 'conversion',
        icon: '🔄',
        title: '16. Conversión CF ↔ HUESO / VIPHUESO',
        table: {
          headers: ['Dirección', 'Regla'],
          rows: [
            ['CF → VIPHUESO', 'Cada X CF = 1 VIPHUESO'],
            ['VIPHUESO → CF', '1 VIPHUESO = X × 0.90 CF'],
            ['CF → HUESO', 'Cada X CF = 1 HUESO'],
            ['HUESO → CF', '1 HUESO = X × 0.90 CF']
          ]
        },
        body: ['X representa la ratio vigente. La conversión hacia CF aplica una diferencia del 10%.']
      },
      {
        id: 'withdrawals',
        icon: '💸',
        title: '17. Retiros y bonificación por nivel',
        table: {
          headers: ['Regla', 'Valor'],
          rows: [
            ['Retiro interno mínimo', '1,000 Mini-HUESO'],
            ['Bonificación', '0.25% del saldo por cada nivel del jugador'],
            ['Fórmula', 'saldo × nivel × 0.0025'],
            ['Ejemplo nivel 10 sobre 1,000', '25 de bonificación · total 1,025']
          ]
        }
      },
      {
        id: 'profile',
        icon: '👤',
        title: '18. Perfil, wallet y referidos',
        bullets: [
          'Perfil del jugador.',
          'Balances y wallet.',
          'Conversión CF.',
          'Referidos.',
          'Stake / Pool.',
          'Retiros.'
        ]
      },
      {
        id: 'visits',
        icon: '🏘️',
        title: '19. Visitar granjas',
        body: [
          'La Estación de Granjas permite visitar otros mundos.',
          'La organización visual y el progreso de cada granja forman parte de la identidad social del jugador.'
        ]
      },
      {
        id: 'arrange',
        icon: '✥',
        title: '20. Ordenar, decorar y capas',
        bullets: [
          'Cuadrícula visible en modo ORDENAR.',
          'Movimiento libre durante el arrastre y alineación al soltar.',
          'Marco visible únicamente sobre la pieza tocada o movida.',
          'Guardado de posiciones.',
          'Edición de carreteras y decoración.',
          'Capas para río, carreteras, campos, decoración, animales, árboles, lámparas, clima y partículas.'
        ]
      },
      {
        id: 'weather',
        icon: '🌦️',
        title: '21. Clima y ambiente',
        body: [
          'El clima es automático y pasa por estados como despejado, lluvia y tormenta.',
          'Después de llover, el terreno puede conservar humedad y charcos durante un tiempo.',
          'La hora UTC del mundo se muestra en la interfaz.'
        ]
      },
      {
        id: 'bigdog',
        icon: '🐕',
        title: '22. Casa Big Dog Bone',
        bullets: [
          'Edificio visible dentro del mapa.',
          'Logo Big Dog Bone.',
          'Pitbull decorativo alrededor de la casa.',
          'Movible con ORDENAR.',
          'Abre Big Dog Bone en una pestaña nueva al interactuar.'
        ]
      },
      {
        id: 'economy',
        icon: '📊',
        title: '23. Economía y equilibrio',
        body: [
          'CityFarm combina producción de recursos, mejoras, desbloqueos, packs, comercio, stake, conversión y retiros.',
          'Agriculture V2 utiliza HUESO como costo de progreso y especialización.',
          'Las cosechas actúan como requisito de actividad, evitando que todo el progreso pueda comprarse de inmediato.',
          'El equilibrio busca que la progresión tome tiempo y mantenga utilidad para los recursos a largo plazo.'
        ]
      },
      {
        id: 'update',
        icon: '✨',
        title: '24. Nueva actualización V2',
        bullets: [
          'Agriculture V2 con seis parcelas y progresión propia.',
          'Nuevas metas de cosecha: 20 / 60 / 100 / 200.',
          'Especializaciones de nivel 5.',
          'Servicios convertidos en edificios del mapa.',
          'Clima automático y efectos posteriores a la lluvia.',
          'ORDENAR con cuadrícula, snap y posiciones persistentes.',
          'Casa Big Dog Bone.',
          'CERRAR SESIÓN / LOG OUT.',
          'Whitepaper interactivo ES / EN.'
        ]
      }
    ]
  },
  en: {
    chooseLanguage: 'CHOOSE YOUR LANGUAGE',
    chooseHint: 'You can change it again at any time.',
    spanish: 'ESPAÑOL',
    english: 'ENGLISH',
    title: 'CITYFARM WHITEPAPER',
    subtitle: 'Gameplay, progression and economy guide',
    index: 'INDEX',
    economicIndex: 'ECONOMIC INDEX',
    backIndex: 'BACK TO INDEX',
    close: 'CLOSE',
    changeLanguage: 'LANGUAGE',
    dynamic: 'DYNAMIC VALUE',
    dynamicNote: 'Market, conversion, pool and some offer rates may change. The game always shows the current value before confirmation.',
    sections: [
      { id:'intro', icon:'🌆', title:'1. What is CityFarm', body:[
        'CityFarm is a farming, production and economy game where each player develops their own world.',
        'Progress is based on producing resources, upgrading lands and facilities, managing inventory, completing activity goals and using ecosystem currencies strategically.',
        'CityFarm V2 turns major features into physical places on the map so the farm becomes the center of the experience.'
      ]},
      { id:'entry', icon:'🚪', title:'2. Entry, account and languages', bullets:[
        'Login with the player account.','Enter CityFarm V2.','Spanish / English selector.','Classic version access when enabled.','Log out returns to login without deleting progress.'
      ]},
      { id:'world', icon:'🗺️', title:'3. CityFarm V2 world', bullets:[
        'Map with roads, river, plots, trees, animals, lamps and buildings.','Player movement across the world.','Proximity interaction: GET CLOSER / INTERACT.','Zoom, visual layers, weather and time cycle.','Services represented by physical buildings.'
      ]},
      { id:'game-loop', icon:'🔁', title:'4. Main gameplay loop', bullets:[
        'Choose a land or facility.','Provide seeds, water, energy or other required resources.','Start production.','Wait for the cycle.','Collect.','Reinvest in upgrades, new plots, trading or specializations.'
      ]},
      { id:'resources', icon:'🎒', title:'5. Resources and currencies', table:{headers:['Resource','Main use'],rows:[
        ['CF','Internal currency/resource used in ecosystem features and conversion.'],['HUESO','Upgrades, unlocks, seeds and Agriculture V2.'],['VIPHUESO','Packs and selected upgrade requirements.'],['Water','Crops, trees and animals.'],['Energy','Production and facility operation.'],['Carbon','Energy production and upgrades.'],['Wood','Produced by trees and used in upgrades.'],['Corn / Wheat','Base crop production.'],['Egg / Milk','Animal production and progression requirements.']
      ]}},
      { id:'packs', icon:'📦', title:'6. Packs and seeds', table:{headers:['Pack','Price','Main contents'],rows:[
        ['Initial','$2','Water 10 · Wheat seed 10 · Energy 2 · Wood 2 · Wheat land ID 3'],
        ['General','$5','Water 40 · Corn seed 20 · Energy 8 · Wood 8 · Corn land ID 1'],
        ['Special','$10','Water 60 · Wheat seed 50 · Corn seed 50 · Energy 30 · Carbon 30 · Wood 30 · Corn ID 2 · Wheat ID 4 · Tree ID 5 · Chicken coop ID 10'],
        ['Water production','$15','Water 30 · Wheat seed 20 · Corn seed 20 · Energy 20 · Carbon 10 · Wood 10 · Tree ID 6 · Water well ID 8 · Cow farm ID 11'],
        ['Energy','$20','Water 40 · Wheat seed 20 · Corn seed 20 · Energy 20 · Carbon 10 · Wood 10 · Tree ID 7 · Power plant ID 9'],
        ['Corn seed','$0.10','20 Corn seed'],['Wheat seed','$0.10','20 Wheat seed']
      ]}},
      { id:'classic-production', icon:'🌾', title:'7. Classic production', table:{headers:['Facility','Base output','Time / cost'],rows:[
        ['Corn','1 seed → 2 Corn','24 h shown in interface · Water by land'],['Wheat','1 seed → 2 Wheat','24 h · Water by land'],['Tree','5 Wood × level','72 h · consumes Water'],['Water well','1 Water per base cycle','4 h · 1 Energy · base capacity 8'],['Power plant','1 Energy per base cycle','4 h · 1 Carbon · base capacity 8'],['Cows','2 Milk × level','24 h · 2 Wheat Bag × level + 3 Water × level'],['Chicken coop','2 Egg × level','24 h · 2 Corn Bag × level + 3 Water × level']
      ]}},
      { id:'classic-upgrades', icon:'⬆️', title:'8. Classic upgrades', body:[
        'Classic lands and facilities use a combination of resources, HUESO and VIPHUESO.',
        'Requirements increase with level. Advanced upgrades consume more Wood, Water, Energy, Carbon, Egg and Milk.',
        'The mill has its own progression up to level 5 and higher levels reduce processing time.'
      ], table:{headers:['Upgrade','HUESO / VIPHUESO reference'],rows:[
        ['LVL 1 → 2','5,000 HUESO + 1 VIPHUESO plus specific resources'],['LVL 2 → 3','5,000–10,000 HUESO + 1 VIPHUESO depending on facility'],['LVL 3 → 4','30,000 HUESO + 5 VIPHUESO in advanced facilities'],['Mill LVL 4 → 5','50,000 HUESO + 10 VIPHUESO + resources']
      ]}},
      { id:'agri-v2', icon:'🚜', title:'9. Agriculture V2: six plots', table:{headers:['Plot','Unlock','Requirement','Base time'],rows:[
        ['1','FREE','Initial','24 h'],['2','5,000 HUESO','1 unlocked plot','24 h'],['3','5,000 HUESO','2 unlocked plots','24 h'],['4','5,000 HUESO','3 unlocked plots','24 h'],['5','50,000 HUESO','4 plots + 25 total harvests','18 h'],['6','100,000 HUESO','5 plots + 75 total harvests','12 h']
      ]}, body:[
        'Each plot can be locked, idle, producing or ready to harvest.','Current crops are corn and wheat.','Values come from the active Agriculture V2 configuration.'
      ]},
      { id:'agri-levels', icon:'⭐', title:'10. Agriculture V2 levels', table:{headers:['Upgrade','Cost','Total harvests'],rows:[
        ['LVL 1 → 2','10,000 HUESO','20'],['LVL 2 → 3','20,000 HUESO','60'],['LVL 3 → 4','40,000 HUESO','100'],['LVL 4 → 5','80,000 HUESO','200']
      ]}, body:['Upgrading requires both HUESO and real activity through harvests.','Levels improve time, yield and/or input consumption depending on the stage.']},
      { id:'specializations', icon:'🌿', title:'11. Specializations', table:{headers:['Specialization','Cost','Benefit'],rows:[
        ['Greenhouse','50,000 HUESO','Time ×0.85 · about 15% faster'],['Advanced Irrigation','60,000 HUESO','Time ×0.95 · inputs ×0.65'],['Intensive Farming','75,000 HUESO','Yield ×1.25 · inputs ×1.15']
      ]}, body:['Requires plot level 5. Each plot can have only one permanent specialization.']},
      { id:'pet-benefits', icon:'🐶', title:'12. Big Dog Bone pet benefits', table:{headers:['Pet level','General production','Water'],rows:[
        ['1–11','-1% time','-1%'],['12–17','-2%','-1%'],['18–23','-2%','-2%'],['24–35','-3%','-2%'],['36–47','-4%','-3%'],['48–53','-5%','-3%'],['54–59','-5%','-4%'],['60+','-6% max','-4% max']
      ]}, body:['The pet must stay in good condition for the benefit to remain active.','It does not directly duplicate harvests; its main benefit is production-time reduction.']},
      { id:'inventory', icon:'🏬', title:'13. Warehouse, objects and tickets', body:['The Warehouse shows resources and inventory.','Objects / Tickets has its own building for special items.','Packs, inventory and objects are part of the management loop.']},
      { id:'trade', icon:'🏪', title:'14. Trade Market', body:['The Trade Market connects production with exchange features.','Prices and availability may vary with the active market.','Always review the current information before confirming a trade.']},
      { id:'stake', icon:'🏦', title:'15. Pool / Stake', table:{headers:['Resource','Points per unit'],rows:[
        ['Water','1'],['Wood','1'],['Carbon','0.25'],['Wheat','1'],['Corn','1'],['Corn Bag','2'],['Wheat Bag','2'],['Egg','3'],['Milk','3'],['Energy','0.5'],['VIPHUESO','0.05'],['HUESO','0.0005']
      ]}, body:['Resources deposited into Stake are converted into points for rewards under the current rules.']},
      { id:'conversion', icon:'🔄', title:'16. CF ↔ HUESO / VIPHUESO conversion', table:{headers:['Direction','Rule'],rows:[
        ['CF → VIPHUESO','Every X CF = 1 VIPHUESO'],['VIPHUESO → CF','1 VIPHUESO = X × 0.90 CF'],['CF → HUESO','Every X CF = 1 HUESO'],['HUESO → CF','1 HUESO = X × 0.90 CF']
      ]}, body:['X is the current ratio. Conversion toward CF applies a 10% difference.']},
      { id:'withdrawals', icon:'💸', title:'17. Withdrawals and level bonus', table:{headers:['Rule','Value'],rows:[
        ['Minimum internal withdrawal','1,000 Mini-HUESO'],['Bonus','0.25% of the withdrawn balance per player level'],['Formula','balance × level × 0.0025'],['Level 10 example on 1,000','25 bonus · total 1,025']
      ]}},
      { id:'profile', icon:'👤', title:'18. Profile, wallet and referrals', bullets:['Player profile.','Balances and wallet.','CF conversion.','Referrals.','Stake / Pool.','Withdrawals.']},
      { id:'visits', icon:'🏘️', title:'19. Visit farms', body:['The Farm Station lets players visit other worlds.','Farm organization and progression become part of each player’s social identity.']},
      { id:'arrange', icon:'✥', title:'20. Arrange, decorate and layers', bullets:[
        'Grid visible in ARRANGE mode.','Free movement while dragging and snap on release.','Frame only on the touched or moved item.','Saved positions.','Road and decoration editing.','Layers for river, roads, fields, decor, animals, trees, lamps, weather and particles.'
      ]},
      { id:'weather', icon:'🌦️', title:'21. Weather and environment', body:['Weather is automatic and cycles through conditions such as clear, rain and storm.','After rain, the terrain may remain wet with puddles for a while.','The world UTC time is shown in the interface.']},
      { id:'bigdog', icon:'🐕', title:'22. Big Dog Bone House', bullets:['Visible building on the map.','Big Dog Bone logo.','Decorative pitbull around the house.','Movable in ARRANGE mode.','Opens Big Dog Bone in a new tab when used.']},
      { id:'economy', icon:'📊', title:'23. Economy and balance', body:[
        'CityFarm combines resource production, upgrades, unlocks, packs, trading, stake, conversion and withdrawals.',
        'Agriculture V2 uses HUESO as a progression and specialization cost.',
        'Harvest requirements add real activity so progression cannot be purchased instantly.',
        'The balance goal is long-term progression and continued utility for resources.'
      ]},
      { id:'update', icon:'✨', title:'24. Current V2 update', bullets:[
        'Agriculture V2 with six plots and its own progression.','New harvest goals: 20 / 60 / 100 / 200.','Level 5 specializations.','Services turned into map buildings.','Automatic weather and post-rain effects.','ARRANGE with grid, snap and persistent positions.','Big Dog Bone House.','LOG OUT button.','Interactive ES / EN Whitepaper.'
      ]}
    ]
  }
};

function SectionContent({ section }) {
  if (!section) return null;
  return (
    <>
      <div className="cfwp-section-heading">
        <span>{section.icon}</span>
        <h2>{section.title}</h2>
      </div>

      {section.body?.map((paragraph, index) => (
        <p key={`p-${index}`} className="cfwp-paragraph">{paragraph}</p>
      ))}

      {section.bullets && (
        <ul className="cfwp-bullets">
          {section.bullets.map((item, index) => <li key={`b-${index}`}>{item}</li>)}
        </ul>
      )}

      {section.table && (
        <div className="cfwp-table-wrap">
          <table className="cfwp-table">
            <thead>
              <tr>
                {section.table.headers.map((header) => <th key={header}>{header}</th>)}
              </tr>
            </thead>
            <tbody>
              {section.table.rows.map((row, rIndex) => (
                <tr key={`r-${rIndex}`}>
                  {row.map((cell, cIndex) => <td key={`c-${rIndex}-${cIndex}`}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function CityFarmWhitepaper({ open, onClose, initialLanguage = 'es' }) {
  const [language, setLanguage] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLanguage(null);
    setSelectedId(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const fallbackLanguage = initialLanguage === 'en' ? 'en' : 'es';
  const safeLanguage = language || fallbackLanguage;
  const copy = CONTENT[safeLanguage];
  const selected = useMemo(
    () => copy.sections.find((section) => section.id === selectedId) || null,
    [copy, selectedId]
  );

  if (!open) return null;

  const chooseLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    setSelectedId(null);
  };

  return (
    <div
      className="cfwp-overlay"
      role="presentation"
      onPointerDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose?.();
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <section
        className="cfwp-modal"
        role="dialog"
        aria-modal="true"
        aria-label="CityFarm Whitepaper"
        onPointerDown={(event) => event.stopPropagation()}
        onPointerMove={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
      >
        {!language ? (
          <div className="cfwp-language-gate">
            <div className="cfwp-book">📖</div>
            <h1>{CONTENT[fallbackLanguage].chooseLanguage}</h1>
            <p>{CONTENT[fallbackLanguage].chooseHint}</p>
            <div className="cfwp-language-choice">
              <button type="button" onClick={() => chooseLanguage('es')}>
                🇪🇸 <strong>ESPAÑOL</strong>
                <small>Whitepaper completo</small>
              </button>
              <button type="button" onClick={() => chooseLanguage('en')}>
                🇺🇸 <strong>ENGLISH</strong>
                <small>Full Whitepaper</small>
              </button>
            </div>
            <button type="button" className="cfwp-gate-close" onClick={onClose}>✕</button>
          </div>
        ) : (
          <>
            <header className="cfwp-header">
              <div>
                <span className="cfwp-kicker">CITYFARM V2</span>
                <h1>📖 {copy.title}</h1>
                <p>{copy.subtitle}</p>
              </div>
              <div className="cfwp-header-actions">
                <div className="cfwp-language-mini" aria-label={copy.changeLanguage}>
                  <button type="button" className={language === 'es' ? 'is-active' : ''} onClick={() => chooseLanguage('es')}>ES</button>
                  <button type="button" className={language === 'en' ? 'is-active' : ''} onClick={() => chooseLanguage('en')}>EN</button>
                </div>
                <button type="button" className="cfwp-close" onClick={onClose}>✕ {copy.close}</button>
              </div>
            </header>

            <div className="cfwp-body">
              <aside className={`cfwp-index ${selected ? 'has-selection' : ''}`}>
                <div className="cfwp-index-title">☰ {copy.index}</div>
                <div className="cfwp-index-list">
                  {copy.sections.map((section) => (
                    <button
                      type="button"
                      key={section.id}
                      className={selectedId === section.id ? 'is-active' : ''}
                      onClick={() => setSelectedId(section.id)}
                    >
                      <span>{section.icon}</span>
                      <strong>{section.title}</strong>
                    </button>
                  ))}
                </div>

                <div className="cfwp-economic-index">
                  <strong>💰 {copy.economicIndex}</strong>
                  <div>
                    {ECONOMIC_INDEX.map((id) => {
                      const section = copy.sections.find((item) => item.id === id);
                      return section ? (
                        <button type="button" key={id} onClick={() => setSelectedId(id)}>
                          {section.icon} {section.title.replace(/^\d+\.\s*/, '')}
                        </button>
                      ) : null;
                    })}
                  </div>
                </div>
              </aside>

              <main className="cfwp-content">
                {!selected ? (
                  <div className="cfwp-welcome">
                    <div className="cfwp-book">📚</div>
                    <h2>{copy.index}</h2>
                    <p>{safeLanguage === 'es'
                      ? 'Selecciona a la izquierda el tema que quieres leer. El contenido aparecerá aquí sin salir del juego.'
                      : 'Choose the topic you want to read from the left. The information will open here without leaving the game.'}</p>
                    <div className="cfwp-highlight-grid">
                      <button type="button" onClick={() => setSelectedId('agri-v2')}>🚜 Agriculture V2</button>
                      <button type="button" onClick={() => setSelectedId('packs')}>📦 Packs</button>
                      <button type="button" onClick={() => setSelectedId('conversion')}>🔄 {safeLanguage === 'es' ? 'Conversión' : 'Conversion'}</button>
                      <button type="button" onClick={() => setSelectedId('economy')}>📊 {safeLanguage === 'es' ? 'Economía' : 'Economy'}</button>
                    </div>
                    <div className="cfwp-dynamic-note">
                      <strong>⚡ {copy.dynamic}</strong>
                      <span>{copy.dynamicNote}</span>
                    </div>
                  </div>
                ) : (
                  <article className="cfwp-article" key={`${safeLanguage}-${selected.id}`}>
                    <button type="button" className="cfwp-back-index" onClick={() => setSelectedId(null)}>
                      ← {copy.backIndex}
                    </button>
                    <SectionContent section={selected} />
                    {['trade','stake','conversion','withdrawals'].includes(selected.id) && (
                      <div className="cfwp-dynamic-note">
                        <strong>⚡ {copy.dynamic}</strong>
                        <span>{copy.dynamicNote}</span>
                      </div>
                    )}
                  </article>
                )}
              </main>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
